import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { AccreditationHeroProps } from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { getActiveSubscriptionCookie } from '~/features/cookies/utils/get-active-subscription-cookie';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';

export function AccreditationHeroSection({
  title,
  description,
  backgroundImages,
  backgroundImageOverlayGradient,
  heroTextMaxWidth,
  formalDegreeKey,
  downloadBrochureButtonSlot = null,
}: {
  formalDegreeKey: FormalDegreeKey;
  downloadBrochureButtonSlot?: ReactNode;
} & AccreditationHeroProps) {
  const clientReady = useClientRender();
  const hasActiveSubscription = clientReady ? getActiveSubscriptionCookie() : null;

  function handleApplyNowClick() {
    trackEvent({
      type: 'click',
      name: 'Masters Page CTA Clicked',
      label: 'hero_enroll_now',
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
  }

  return (
    <HeroLayout
      backgroundImageOverlayGradient={backgroundImageOverlayGradient}
      backgroundImageSlot={
        <>
          <Box display={{ base: 'block', md: 'none' }} inset={0} position='absolute'>
            <Image
              alt=''
              quality={100}
              sizes='100vw'
              src={backgroundImages.mobile}
              style={{ objectFit: 'cover', objectPosition: backgroundImages.objectPosition?.mobile ?? 'center' }}
              fill
              priority
            />
          </Box>
          <Box display={{ base: 'none', md: 'block' }} inset={0} position='absolute'>
            <Image
              alt=''
              quality={100}
              sizes='100vw'
              src={backgroundImages.desktop}
              style={{ objectFit: 'cover', objectPosition: backgroundImages.objectPosition?.desktop ?? 'center' }}
              fill
              priority
            />
          </Box>
        </>
      }
      ctaSlot={
        <Flex flexWrap='wrap' gap={{ base: '16px', md: '24px' }}>
          <ButtonLink
            buttonProps={{
              leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
              size: 'icon-left',
              variant: 'solid',
              colorScheme: 'blue',
              width: { base: '100%', md: 'fit-content' },
              onClick: handleApplyNowClick,
            }}
            linkProps={{
              href: hasActiveSubscription
                ? `/checkout/individual/monthly/credit-card?degreeApplication=true&formalDegreeKey=${formalDegreeKey}`
                : `/checkout/degree-application/purchase/credit-card?formalDegreeKey=${formalDegreeKey}`,
            }}
          >
            Enroll Now
          </ButtonLink>
          {downloadBrochureButtonSlot}
        </Flex>
      }
      textAndCtaContainerProps={{
        gap: { base: '24px', md: '40px' },
      }}
      textContentSlot={
        <Flex
          flexDirection='column'
          gap={{ base: '16px', md: '24px' }}
          maxWidth={heroTextMaxWidth ?? { base: '100%', md: '600px' }}
          whiteSpace='pre-wrap'
          pt={{
            base: '120px',
            md: '0',
          }}
        >
          <Heading as='h1' color='white' letterSpacing='0.01em' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
            {title}
          </Heading>
          <Text color='gray.300' fontSize='lg' lineHeight='lg'>
            {description}
          </Text>
        </Flex>
      }
    />
  );
}
